import * as React from 'react';
import { cn } from '../../lib/cn';
import { useFormFieldContext } from '../form_field/form_field';

export interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size'
> {
  /** Visual size of the input. */
  size?: 'sm' | 'md' | 'lg';
  error?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

/**
 * Input types whose value is always Latin-script, left-to-right data (ADR-021
 * § 8): e-mail addresses, URLs, phone numbers. Under an RTL page these must
 * keep `dir="ltr"` or the `@`, `.` and `/` punctuation reorders visually.
 */
const LTR_INPUT_TYPES: ReadonlySet<string> = new Set(['email', 'url', 'tel']);

/** Class tokens that mark a code/ID field (monospace = codes, never prose). */
const CODE_FIELD_CLASS = /(^|\s)(font-mono|mono)(\s|$)/;

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      size,
      error,
      leftIcon,
      rightIcon,
      style,
      onWheel,
      ...htmlProps
    },
    ref,
  ) => {
    // `dir` goes on the wrapper so the leading/trailing icon slots and the
    // inline padding resolve in the same direction as the text (see
    // MoneyInput); on the bare input an RTL page would put the icon on the
    // opposite side of the padding gutter.
    const forceLtr =
      (htmlProps.type !== undefined && LTR_INPUT_TYPES.has(htmlProps.type)) ||
      (className !== undefined && CODE_FIELD_CLASS.test(className));
    const resolvedSize = size ?? 'md';
    const formFieldContext = useFormFieldContext();
    const hasLeft = leftIcon !== null && leftIcon !== undefined;
    const hasRight = rightIcon !== null && rightIcon !== undefined;

    /**
     * A focused `<input type="number">` treats the mouse wheel as a stepper, so
     * scrolling the PAGE silently edits whatever number field happens to hold
     * focus — a money amount becomes 4.99 instead of 5.00 with no interaction
     * the user would recognise as editing. Blurring on wheel keeps the page
     * scrolling normally while making the value inert.
     *
     * Only `type="number"` is affected; every other input ignores the wheel.
     * A caller-supplied `onWheel` still runs (and may preventDefault first).
     */
    const handleWheel = React.useCallback(
      (event: React.WheelEvent<HTMLInputElement>) => {
        onWheel?.(event);
        if (event.defaultPrevented) return;
        if (
          htmlProps.type === 'number' &&
          document.activeElement === event.currentTarget
        ) {
          event.currentTarget.blur();
        }
      },
      [onWheel, htmlProps.type],
    );

    // Always render the same wrapper + <input> tree regardless of whether an
    // icon is present. Toggling leftIcon/rightIcon present↔absent must never
    // remount the <input> — a different return shape (bare <input> vs
    // <div><input>) causes React to unmount and recreate the DOM node, which
    // drops focus mid-typing (same root cause fixed in apps/ledger-web Input #15332).
    return (
      <div className="relative w-full" dir={forceLtr ? 'ltr' : undefined}>
        <input
          ref={ref}
          className={cn(
            'nv-input',
            // Explicit height so an `<Input>` matches the `MoneyInput` /
            // `StyledSelect` / `DateInput` beside it instead of landing a couple
            // of px off on its own padding (visible on the tax hub's Betting &
            // Gaming Levy row, where a padding-derived ~38px input sat next to
            // 36px controls).
            //
            // Deliberately a BASE-ONLY rung with no `sm:` variant. A responsive
            // pair (`h-11 sm:h-9`) is not overridable by a call site, because
            // tailwind-merge groups `h-*` and `sm:h-*` separately, so the `sm:`
            // rung keeps winning above 640px — that is exactly what broke ~50
            // `<Button className="h-9|h-10">` call sites when the same idea was
            // tried there (see `../button/button.tsx`). A single token merges
            // cleanly: `cn('h-9', 'h-10')` → `h-10` at every width.
            //
            // Cost of the base-only choice: no taller mobile touch target, so on
            // mobile this stays 36px while `DateInput`/`Select` step to `h-11`.
            // That is unchanged from the padding-derived behaviour, not a new
            // regression, and closing it needs the override sites migrated.
            resolvedSize === 'sm' && 'nv-input-sm h-8',
            resolvedSize === 'md' && 'h-9',
            resolvedSize === 'lg' && 'nv-input-lg h-10',
            error && 'nv-field-error',
            className,
          )}
          style={
            hasLeft || hasRight
              ? {
                  paddingInlineStart: hasLeft ? '2.25rem' : undefined,
                  paddingInlineEnd: hasRight ? '2.25rem' : undefined,
                  ...style,
                }
              : style
          }
          aria-describedby={formFieldContext?.describedBy}
          aria-invalid={formFieldContext?.ariaInvalid}
          onWheel={handleWheel}
          {...htmlProps}
        />
        {hasLeft && (
          <span className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-ink-muted">
            {leftIcon}
          </span>
        )}
        {hasRight && (
          <span className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-ink-muted">
            {rightIcon}
          </span>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';
